import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddressBookOverlayIsOpen from "@insite/client-framework/Store/Components/AddressBookOverlay/Handlers/SetAddressBookOverlayIsOpen";
import { getCurrentUserIsGuest, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentShipToState,
    getShipTosDataView,
    getShipToState,
} from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import loadShipTos from "@insite/client-framework/Store/Pages/Addresses/Handlers/LoadShipTos";
import setUseBillingAddress from "@insite/client-framework/Store/Pages/CheckoutShipping/Handlers/SetUseBillingAddress";
import translate from "@insite/client-framework/Translate";
import {
    AddressFieldDisplayCollectionModel,
    BaseAddressModel,
    CountryModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import ShipToSelector, { ShipToSelectorStyles } from "@insite/content-library/Components/ShipToSelector";
import CreateNewAddressModal, {
    CreateNewAddressModalStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/CreateNewAddressModal";
import EditExistingAddressModal, {
    EditExistingAddressModalStyles,
} from "@insite/content-library/Widgets/CheckoutShipping/EditExistingAddressModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps } from "@insite/mobius/CheckboxGroup";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Radio, { RadioPresentationProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupPresentationProps, RadioGroupProps } from "@insite/mobius/RadioGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CreateAccountExistingFormStyles } from "@tCooneyBrothers/Components/CreateAccountExistingForm";
import setUseOneTimeAddress from "@tCooneyBrothers/Overrides/Store/Pages/CheckoutShipping/Handlers/SetUseOneTimeAddress";
import ShippingAddressForm from "@tCooneyBrothers/Overrides/Widgets/CheckoutShipping/ShippingAddressForm";
import React, { FC, useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

interface OwnProps {
    address?: ShipToModel;
    currentBillToId: string | undefined;
    newAddress?: ShipToModel;
    oneTimeAddress?: ShipToModel;
    countries: CountryModel[];
    addressFieldDisplayCollection: AddressFieldDisplayCollectionModel;
    onChange: (address: ShipToModel, isEdit: boolean) => void;
    showUseBillingAddress?: boolean;
    isUseBillingAddressDisabled?: boolean;
    onClickUseBillingAddress?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    extendedStyles?: ShippingAddressStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cart = cartId ? getCartState(state, cartId).value : getCurrentCartState(state).value;

    return {
        currentUserIsGuest: getCurrentUserIsGuest(state),
        shipTosDataView: getShipTosDataView(state, state.pages.addresses.getShipTosParameter),
        shipToState: getShipToState(state, cart?.shipToId),
        billToState: getBillToState(state, cart?.billToId),
        useBillingAddress: state.pages.checkoutShipping.useBillingAddress,
        useOneTimeAddress: state.pages.checkoutShipping.useOneTimeAddress,
        allowCreateNewShipToAddress: getSettingsCollection(state).customerSettings.allowCreateNewShipToAddress,
        shippingAddressFormState: state.pages.checkoutShipping.shippingAddressFormState,
        isShippingAddressUpdateRequired: state.pages.checkoutShipping.isShippingAddressUpdateRequired,
    };
};

const mapDispatchToProps = {
    setUseBillingAddress,
    setUseOneTimeAddress,
    loadShipTos,
    setAddressBookOverlayIsOpen,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme>;

export interface ShippingAddressStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    headingText?: TypographyPresentationProps;
    useSavedAdd?: RadioPresentationProps;
    selectSavedAddressLink?: LinkPresentationProps;
    addressBookModal?: ModalPresentationProps;
    shipToSelector?: ShipToSelectorStyles;
    addressBookModalButtonsWrapper?: InjectableCss;
    // addressBookModalCancelButton?: ButtonPresentationProps;
    createNewAddressModal?: CreateNewAddressModalStyles;
    editAddressModal?: EditExistingAddressModalStyles;
    oneTimeAddressGridItem?: GridItemProps;
    oneTimeGridItem?: GridItemProps;
    registeredUserOneTimeAddressGridItem?: GridItemProps;
    addressDisplayAndFormGridItem?: GridItemProps;
    addressDisplay?: AddressInfoDisplayStyles;
    useBillingAddressButton?: ButtonPresentationProps;
    CreateAccountExistingFormStyles?: CreateAccountExistingFormStyles;
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    shippingAddressImg?: InjectableCss;
}

export const shippingAddressStyles: ShippingAddressStyles = {
    container: {
        gap: 20,
        css: css`
            input[type="radio"] {
                &:focus + label::before {
                    box-shadow: none !important;
                }
            }
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            align-items: center;
            > h3 {
                margin-bottom: 0;
                margin-top: 10px;
                padding-top: 0;
                padding-bottom: 14px;
            }
        `,
    },
    headingText: {
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    useSavedAdd: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            margin-bottom: 30px;
                        `,
                        css`
                            margin-bottom: 30px;
                        `,
                        null,
                        css`
                            margin-bottom: 30px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    selectSavedAddressLink: {
        css: css`
            margin-left: 10px;
            > span {
                font-weight: 100;
                font-size: 12px;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            &[class^="StyledButton"] {
                                position: absolute !important;
                                bottom: -20px;
                                margin: 0 !important;
                                left: 26px;
                            }
                        `,
                        css`
                            position: static !important;
                            margin-left: 10px !important;
                        `,
                        css`
                            position: absolute;
                            bottom: -20px;
                            margin: 0;
                            left: 26px;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    addressBookModal: {
        sizeVariant: "medium",
        cssOverrides: {
            modalContent: css`
                overflow-y: initial;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                padding-bottom: 80px;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
    },
    addressBookModalButtonsWrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            margin-top: -3rem;
        `,
    },
    oneTimeAddressGridItem: {
        width: 12,
        css: css`
            padding-top: 0;
            > div[class^="RadioGroupStyle"] {
                flex-direction: row;
                align-items: flex-end;
                width: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            display: block;
                        `,
                        null,
                        null,
                        null,
                        null,
                    ])}
                > div[class^="RadioStyle"] {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                width: 100%;
                            `,
                            css`
                                width: 50%;
                            `,
                            css`
                                width: 50%;
                            `,
                            css`
                                width: 40%;
                            `,
                            css`
                                width: 25%;
                            `,
                        ])}
                }
            }
        `,
    },
    oneTimeGridItem: {
        width: 12,
    },
    registeredUserOneTimeAddressGridItem: {
        width: 12,
        css: css`
            padding-top: 0;
            > div[class^="RadioGroupStyle"] {
                flex-direction: row;
                align-items: flex-end;
                width: 100%;
                display: block;
                > div[class^="RadioStyle"] {
                    width: 100%;
                }
            }
        `,
    },
    addressDisplayAndFormGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    useBillingAddressButton: {
        variant: "secondary",
        css: css`
            margin-top: 20px;
        `,
    },
    CreateAccountExistingFormStyles: {
        ...CreateAccountExistingFormStyles,
        sectionHeading: {
            css: css`
                font-weight: bold;
                border-bottom: 2px solid #ef3742;
                padding: 0 9px 9px;
                padding-left: 15px;
                width: 100%;
                background: #f8f9fa;
                margin-bottom: 25px;
                margin-top: 25px;
                font-size: 15px;
            `,
        },
    },
    centeringWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            height: 100px;
            width: 100%;
            justify-content: center;
            align-items: center;
            position: relative;
        `,
    },
    shippingAddressImg: {
        css: css`
            width: 30px;
            top: 13px;
            margin-right: 10px;
        `,
    },
};

const ShippingAddress: FC<Props> = ({
    address,
    newAddress,
    oneTimeAddress,
    onChange,
    currentUserIsGuest,
    useBillingAddress,
    useOneTimeAddress,
    allowCreateNewShipToAddress,
    setUseBillingAddress,
    setUseOneTimeAddress,
    extendedStyles,
    currentBillToId,
    isUseBillingAddressDisabled,
    shippingAddressFormState,
    showUseBillingAddress,
    isShippingAddressUpdateRequired,
    countries,
    addressFieldDisplayCollection,
    theme,
    setAddressBookOverlayIsOpen,
    shipTosDataView,
    loadShipTos,
    shipToState,
    billToState,
}) => {
    const [isCustomerSelectorModalOpen, setIsCustomerSelectorModalOpen] = useState(false);
    const [isCreateNewAddressModalOpen, setIsCreateNewAddressModalOpen] = useState(false);
    const [isEditAddressModalOpen, setIsEditAddressModalOpen] = useState(false);
    const [addressBeingEdited, setAddressBeingEdited] = useState<ShipToModel | undefined>(undefined);
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(shippingAddressStyles, extendedStyles));
    const [billingState, setBillingState] = useState(false);
    const [oneTimeState, setOneTimeState] = useState(false);
    const [savedAdd, setSavedAdd] = useState(false);

    const [billToShipToSame, setBillToShipToSame] = React.useState<boolean>(false);

    const [showSavedAddress, setShowSavedAddress] = React.useState<boolean>(false);

    const [arrayLength, setArrayLength] = React.useState<any>(0);

    useEffect(() => {
        if (billToShipToSame) {
            setBillingState(true);
            setSavedAdd(false);
        }
        if (!billToShipToSame) {
            setSavedAdd(true);
            setBillingState(false);
        }
    }, [billToShipToSame]);

    useEffect(() => {
        if (!useBillingAddress && !useOneTimeAddress && savedAdd && !billToShipToSame) {
            setSavedAdd(true);
            setBillingState(false);
            setOneTimeState(false);
        } else if (useBillingAddress && !savedAdd && !useOneTimeAddress && billToShipToSame) {
            setBillingState(true);
            // localStorage.setItem("savedAdd", JSON.stringify(false));
            setSavedAdd(false);
            setOneTimeState(false);
        } else if (useOneTimeAddress && !savedAdd && !useBillingAddress) {
            setOneTimeState(true);
            // localStorage.setItem("savedAdd", JSON.stringify(false));
            setSavedAdd(false);
            setBillingState(false);
        }
    }, [useBillingAddress, useOneTimeAddress, savedAdd]);

    // useEffect(() => {
    //     const savedAddValue = localStorage.getItem("savedAdd");
    //     if (savedAddValue) {
    //         setSavedAdd(JSON.parse(savedAddValue));
    //     }
    // }, []);

    // useEffect(() => {
    //     if (currentUserIsGuest) {
    //         setBillingState(true);
    //     }
    // });

    useEffect(() => {
        if (addressBeingEdited) {
            setIsEditAddressModalOpen(true);
        }
    }, [addressBeingEdited]);

    React.useEffect(() => {
        if (!shipTosDataView.value && !shipTosDataView.isLoading) {
            loadShipTos();
        }
        setArrayLength(shipTosDataView.value?.length);
    }, [shipTosDataView]);

    React.useEffect(() => {
        if (shipToState.id === billToState.id) {
            setBillToShipToSame(true);
        }
    }, [shipToState.id, billToState.id]);
    React.useEffect(() => {
        if (arrayLength === 0) {
            setShowSavedAddress(true);
            // setBillingState(true);
        } else {
            setShowSavedAddress(false);
        }
    }, [arrayLength, billToShipToSame, useBillingAddress]);

    const addressTypeChangeHandler: RadioGroupProps["onChangeHandler"] = event => {
        if (currentUserIsGuest) {
            if (event.target.value === "onetime") {
                setUseOneTimeAddress({
                    useOneTimeAddress: true,
                });
                setOneTimeState(true);
            } else {
                setUseBillingAddress({
                    useBillingAddress: true,
                });
                setBillingState(true);
            }
        }
        if (event.target.value === "onetime") {
            setUseOneTimeAddress({
                useOneTimeAddress: true,
            });
            setOneTimeState(true);
            setBillingState(false);
            setSavedAdd(false);
        } else if (event.target.value === "billing") {
            setUseBillingAddress({
                useBillingAddress: true,
            });
            setBillingState(true);
            setOneTimeState(false);
            setSavedAdd(false);
        } else if (event.target.value === "savedAdd") {
            selectAddressClickHandler();
            setBillingState(false);
            setOneTimeState(false);
            setSavedAdd(true);
        }
    };

    const selectAddressClickHandler = (radioState?: string) => {
        if(radioState === "savedAdd") {
            setSavedAdd(true);
            setOneTimeState(true);
        }
        theme.addressBook?.useOverlay
            ? setAddressBookOverlayIsOpen({ isOpen: true, isBillTo: false })
            : setIsCustomerSelectorModalOpen(true);
    };

    const customerSelectorModalCloseHandler = () => setIsCustomerSelectorModalOpen(false);

    const setSavedHandler = () => {
        setBillingState(false);
        setOneTimeState(false);
        setSavedAdd(true);
    };

    const selectCustomerHandler = (shipTo: ShipToModel) => {
        customerSelectorModalCloseHandler();
        onChange(shipTo, false);
        // setSavedHandler();
    };

    const createNewAddressHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event.preventDefault();
        setIsCreateNewAddressModalOpen(true);
    };

    const createNewAddressModalCloseHandler = () => setIsCreateNewAddressModalOpen(false);

    const newShippingAddressFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {
        onChange(address, true);
        createNewAddressModalCloseHandler();
        customerSelectorModalCloseHandler();
        toasterContext.addToast({
            messageType: "success",
            body: translate("Address has been created."),
        });
    };

    const editCustomerHandler = (
        event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        customer: BaseAddressModel,
    ) => {
        event.preventDefault();
        setAddressBeingEdited(customer as ShipToModel);
    };

    const editAddressModalCloseHandler = () => {
        setIsEditAddressModalOpen(false);
        setAddressBeingEdited(undefined);
    };

    const editAddressFormSubmitHandler = (_: React.FormEvent<HTMLFormElement>, address: ShipToModel) => {
        onChange(address, true);
        editAddressModalCloseHandler();
        customerSelectorModalCloseHandler();
        toasterContext.addToast({
            messageType: "success",
            body: translate("Address updated."),
        });
    };
    const StyledSection = getStyledWrapper("section");

    return (
        <GridContainer {...styles.container} data-test-selector="checkoutShipping_shippingAddress">
            <GridItem {...styles.headingGridItem}>
                <Typography {...CreateAccountExistingFormStyles.sectionHeading} as="h3">
                    <LazyImage
                        {...styles.shippingAddressImg}
                        src="/UserFiles/images/addresspage/checkout-ship-to-icon.png"
                    ></LazyImage>
                    {translate("Shipping Address")}
                </Typography>
                <Modal
                    {...styles.addressBookModal}
                    headline={translate("Address Book")}
                    isOpen={isCustomerSelectorModalOpen}
                    handleClose={customerSelectorModalCloseHandler}
                    data-test-selector="checkoutShipping_addressModal"
                >
                    <ShipToSelector
                        currentShipTo={useOneTimeAddress && shippingAddressFormState ? undefined : address}
                        currentBillToId={currentBillToId}
                        newAddress={newAddress}
                        onSelect={selectCustomerHandler}
                        onEdit={editCustomerHandler}
                        onCreateNewAddressClick={newAddress && createNewAddressHandler}
                        extendedStyles={styles.shipToSelector}
                    />
                    <StyledWrapper {...styles.addressBookModalButtonsWrapper}>
                        <Link
                            // {...styles.addressBookModalCancelButton}
                            onClick={customerSelectorModalCloseHandler}
                            data-test-selector="addressModal_cancel"
                        >
                            {translate("Cancel")}
                        </Link>
                    </StyledWrapper>
                </Modal>
                {newAddress && (
                    <CreateNewAddressModal
                        newAddress={newAddress}
                        isModalOpen={isCreateNewAddressModalOpen}
                        onModalClose={createNewAddressModalCloseHandler}
                        onFormSubmit={newShippingAddressFormSubmitHandler}
                    />
                )}
                {addressBeingEdited && (
                    <EditExistingAddressModal
                        newAddress={addressBeingEdited}
                        isModalOpen={isEditAddressModalOpen}
                        onModalClose={editAddressModalCloseHandler}
                        onFormSubmit={editAddressFormSubmitHandler}
                    />
                )}
            </GridItem>
            {oneTimeAddress && showUseBillingAddress ? (
                <GridItem
                    {...(currentUserIsGuest
                        ? { ...styles.oneTimeAddressGridItem }
                        : { ...styles.registeredUserOneTimeAddressGridItem })}
                >
                    <RadioGroup
                        value={
                            shippingAddressFormState && savedAdd
                                ? "savedAdd"
                                : shippingAddressFormState && billingState
                                ? "billing"
                                : shippingAddressFormState && oneTimeState
                                ? "onetime"
                                : ""
                        }
                        onChangeHandler={addressTypeChangeHandler}
                    >
                        <Radio value="billing">{translate("Use Billing Address")}</Radio>
                        <Radio value="onetime" data-test-selector="checkoutShipping_useOneTimeAddress">
                            {currentUserIsGuest
                                ? translate("Use a Different Address")
                                : translate("Use One-Time Ship To Address")}
                        </Radio>
                        {showSavedAddress === false && (
                            <Radio value="savedAdd" {...styles.useSavedAdd}>
                                {translate("Use Saved Address Below")}
                                {(!currentUserIsGuest || (currentUserIsGuest && allowCreateNewShipToAddress)) &&
                                arrayLength > 1 ? (
                                    <Link
                                        {...styles.selectSavedAddressLink}
                                        type="button"
                                        onClick={() => selectAddressClickHandler("savedAdd")}
                                        data-test-selector="checkoutShipping_selectSavedAddress"
                                    >
                                        {translate("Select a different saved address")}
                                    </Link>
                                ) : null}
                            </Radio>
                        )}
                    </RadioGroup>
                </GridItem>
            ) : (
                <>
                    {oneTimeAddress && (
                        <GridItem {...styles.oneTimeGridItem}>
                            <RadioGroup
                                value={shippingAddressFormState ? (oneTimeState ? "onetime" : "savedAdd") : ""}
                                onChangeHandler={addressTypeChangeHandler}
                            >
                                <Radio value="onetime" disabled={currentUserIsGuest}>
                                    {translate("Use One-Time Ship To Address")}
                                </Radio>
                                {showSavedAddress === false && (
                                    <Radio value="savedAdd">
                                        {translate("Use Saved Address Below")}
                                        {arrayLength > 1 ? (
                                            <Link
                                                {...styles.selectSavedAddressLink}
                                                type="button"
                                                onClick={() => selectAddressClickHandler("savedAdd")}
                                                data-test-selector="checkoutShipping_selectSavedAddress"
                                            >
                                                {translate("Select a different saved address")}
                                            </Link>
                                        ) : null}
                                    </Radio>
                                )}
                            </RadioGroup>
                        </GridItem>
                    )}
                    {showUseBillingAddress && (
                        <GridItem {...styles.oneTimeGridItem}>
                            <RadioGroup
                                value={shippingAddressFormState ? (billingState ? "billing" : "savedAdd") : ""}
                                onChangeHandler={addressTypeChangeHandler}
                            >
                                <Radio value="billing" disabled={isUseBillingAddressDisabled}>
                                    {translate("Use Billing Address")}
                                </Radio>
                                {showSavedAddress === false && (
                                    <Radio value="savedAdd">
                                        {translate("Use Saved Address Below")}
                                        {arrayLength > 1 ? (
                                            <Link
                                                {...styles.selectSavedAddressLink}
                                                type="button"
                                                onClick={() => selectAddressClickHandler("savedAdd")}
                                                data-test-selector="checkoutShipping_selectSavedAddress"
                                            >
                                                {translate("Select a different saved address")}
                                            </Link>
                                        ) : null}
                                    </Radio>
                                )}
                            </RadioGroup>
                        </GridItem>
                    )}
                </>
            )}
            <GridItem {...styles.addressDisplayAndFormGridItem}>
                {(useOneTimeAddress || (!currentUserIsGuest && isShippingAddressUpdateRequired)) && (
                    <ShippingAddressForm
                        countries={countries}
                        fieldDisplay={addressFieldDisplayCollection}
                        currentUserIsGuest={currentUserIsGuest}
                    />
                )}
                {!useOneTimeAddress && !currentUserIsGuest && !isShippingAddressUpdateRequired && address && (
                    <AddressInfoDisplay
                        {...address}
                        state={address.state ? address.state.abbreviation : undefined}
                        country={address.country ? address.country.abbreviation : undefined}
                        extendedStyles={styles.addressDisplay}
                    />
                )}
            </GridItem>
        </GridContainer>
    );
};
export default connect(mapStateToProps, mapDispatchToProps)(withTheme(ShippingAddress));
